import type {
  TStaffAvailability,
  TStaffAwayReason,
  TStaffHoldKind,
  TStaffSubRole,
} from '@/services/staff';

type TAdminColumn = {
  key: string;
  label: string;
  width: string;
};

type TMatrixColumn = {
  role: TStaffSubRole;
  label: string;
  width: string;
  isLocked: boolean;
};

type TInviteStep = {
  key: string;
  title: string;
  caption: string;
};

type TAdminManageTabID = 'staff' | 'matrix' | 'settings';

type THoldKindCopy = {
  one: string;
  many: string;
};

type TAdminManageTab = {
  value: TAdminManageTabID;
  label: string;
};

const ADMIN_MANAGE_COPY = {
  title: 'Admin Manage',
  subtitle: 'Who can touch what — and what no one can.',
  invite: 'Invite staff',
  edit: 'Edit',
  settings: 'Settings',
  blank: '—',
  emptyTitle: 'No staff users yet.',
  emptyDescription: 'Invite the first one with the button above.',
  errorDescription: 'Nothing was loaded. Reload the page or try again in a moment.',
} as const;

const STAFF_SETTINGS_COPY = {
  pickerLabel: 'Staff member',
  pickerPlaceholder: 'Search by name or email',
  pickerEmpty: 'No one matches.',
  emptyTitle: 'Pick a staff member',
  emptyDescription: 'Search in the top right corner, or open someone through Settings in Staff List.',
  notFoundTitle: 'This staff member is not in the list.',
  notFoundDescription: 'They may have been removed. Pick someone else above.',
  firstSignIn: 'First sign-in',
  status: 'Status',
  promoteTitle: 'Promote',
  promoteDescription: 'The role decides what this person can see and do in the panel.',
  roleLabel: 'Change role',
  roleSave: 'Save role',
  lastSuperAdmin: 'This is the last super admin. Make someone else a super admin before changing this role.',
  inactiveNoSuperAdmin: 'An inactive employee cannot become a super admin. Make them active first.',
  superAdminNotInactive: 'A super admin cannot be switched off. Change their role in Promote first.',
  availabilityTitle: 'Availability',
  integrationsTitle: 'Integrations',
  calendlyLinkedTo: 'Linked to',
  calendlyLinked: 'Linked',
  connect: 'Connect',
  disconnect: 'Disconnect',
  calendarUnlinked: 'Calendar unlinked',
  connectConfirm: 'Connect',
  cancel: 'Cancel',
} as const;

const STAFF_TRANSITION_COPY = {
  roleTitle: 'Change role',
  deactivateTitle: 'Deactivate',
  reactivateTitle: 'Reactivate',
  checking: 'Checking what this change touches…',
  moveDescription: 'These cases cannot stay with this person after the change. Choose who takes them.',
  handoverPlaceholder: 'Choose who takes them',
  roundRobin: 'Round-robin — spread across the team',
  callsTitle: 'Calls to settle first',
  callsEmpty: 'Open Interviews to find these calls.',
  movedTitle: 'Done. The cases went to:',
  movedNothing: 'Done. No cases had to move.',
  cancel: 'Cancel',
  apply: 'Apply',
  close: 'Close',
  applied: 'Changes saved',
} as const;

const HOLD_KIND_COPY: Record<TStaffHoldKind, THoldKindCopy> = {
  'applicant': { one: 'applicant', many: 'applicants' },
  'member': { one: 'member', many: 'members' },
};

const TRANSITION_KIND_TOKEN = '{kind}';

const TRANSITION_BLOCKER_COPY: Record<string, string> = {
  'interviews_pending': 'This person has interview calls that are booked or still waiting for a decision. '
    + 'Decide or release each call, then try again.',
  'last_super_admin': 'This is the last super admin. Make someone else a super admin before changing this role.',
  'staff_super_admin_not_inactive': 'A super admin cannot be switched off. Change their role first.',
  'handover_missing': 'Choose who takes the {kind}.',
  'no_receiver': 'Round-robin found nobody free to take the {kind}. Pick a person.',
  'receiver_invalid': 'The person picked can no longer take the {kind}. Pick someone else.',
};

const TRANSITION_ERROR_COPY: Record<string, string> = {
  '42501': 'You no longer have the right to make this change.',
  'P0002': 'This staff member no longer exists.',
  '40P01': 'Another change for the same people ran at the same moment. Try again.',
  'no_change': 'Nothing to change — the role and status are already set.',
  'handover_shape': 'The hand-over choice was not understood. Close this window and try again.',
  'use_set_staff_availability': 'Use Active or Away for this change.',
  'interviews_pending': TRANSITION_BLOCKER_COPY.interviews_pending,
  'last_super_admin': TRANSITION_BLOCKER_COPY.last_super_admin,
  'staff_super_admin_not_inactive': TRANSITION_BLOCKER_COPY.staff_super_admin_not_inactive,
  'handover_missing': 'Choose who takes the cases before applying.',
  'no_receiver': 'Round-robin found nobody free to take the cases. Pick a person.',
  'receiver_invalid': 'The person picked can no longer take these cases. Pick someone else.',
};

const ADMIN_MANAGE_TAB_PARAM = 'tab';

const ADMIN_MANAGE_STAFF_PARAM = 'staff';

const ADMIN_MANAGE_TABS: readonly TAdminManageTab[] = [
  { value: 'staff', label: 'Staff List' },
  { value: 'matrix', label: 'Permission Matrix' },
  { value: 'settings', label: 'Settings' },
];

const STAFF_CALENDAR_COPY = {
  linked: 'Linked',
  notLinked: 'Not linked',
} as const;

const TOTP_COPY = {
  enrolled: 'Enrolled',
  pending: 'Pending first sign-in',
  reset: 'Not enrolled — reset',
} as const;

const MATRIX_COPY = {
  title: 'Permission matrix',
  subtitle: 'What each role can do. The Super Admin column is locked.',
  area: 'Area of responsibility',
  notAllowed: '—',
  locked: 'Locked',
  allowed: 'Allowed',
  footnote: 'No permission exists to override a violent-crime verification failure — absent by design, not configuration.',
} as const;

const ROLES_COPY = {
  title: 'Roles',
  locked: 'system · locked',
} as const;

const INVITE_COPY = {
  title: 'Invite staff',
  description: 'The invitation goes to the address you enter. From there, the new staff member goes through four steps:',
  tracking: 'After sending, the invite shows in Staff users on this screen as “Pending first sign-in”. '
    + 'Edit on that row gets a new link, resends the email or revokes the invite.',
  emailLabel: 'Email',
  emailPlaceholder: 'name@example.com',
  roleLabel: 'Role',
  rolePlaceholder: 'Pick a role',
  submit: 'Send invite',
  cancel: 'Cancel',
  sent: 'Invite sent',
  sentTo: 'Invitation sent to',
  notSent: 'The invitation is saved, but the email did not go out. Get a link and pass it on yourself.',
  worksUntil: 'The link works until',
  getLink: 'Get link',
  linkLabel: 'Invite link',
  linkHint: 'Send it to the invited address only. A new link replaces the previous one at once.',
  calendar: 'Next, invite the same address to the Calendly organization. Once they have one ready event, it links on their next sign-in; '
    + 'if it does not, link it in Edit on their row. Until then no applicants reach them.',
  copyLink: 'Copy link',
  copied: 'Link copied',
  done: 'Done',
} as const;

const INVITE_ERROR_COPY: Record<string, string> = {
  'account_exists': 'This address already has an account in The 500.',
  'invite_pending': 'This address already has a pending invite. Open it in the list to get a new link.',
  'consumed': 'This invite has already been used.',
  'revoked': 'This invite was revoked.',
  'not_found': 'This invite no longer exists.',
};

const PENDING_INVITE_COPY = {
  title: 'Pending invite',
  invited: 'Invited',
  expired: 'Invite expired',
  worksUntil: 'The link works until',
  expiredOn: 'The link expired on',
  emailNotSent: 'The invitation email has not gone out.',
  getLink: 'Get new link',
  resend: 'Resend email',
  resent: 'Invitation email sent',
  revoke: 'Revoke invite',
  revoked: 'Invite revoked',
  revokeConfirmTitle: 'Revoke the invitation for',
  revokeConfirmDescription: 'The link stops working at once. The same address can be invited again later.',
  revokeConfirm: 'Revoke',
  cancel: 'Cancel',
  close: 'Close',
} as const;

const EDIT_COPY = {
  availabilityLabel: 'Status',
  reasonLabel: 'Reason',
  untilLabel: 'Back on (optional)',
  save: 'Save',
  cancel: 'Cancel',
  saved: 'Status saved',
  totpTitle: 'Two-step verification',
  totpHint: 'Resetting removes the enrolled authenticator. The admin enrols a new one on next sign-in.',
  resetTotp: 'Reset TOTP',
  totpReset: 'TOTP reset',
  resetConfirmTitle: 'Reset two-step for',
  resetConfirmDescription: 'Their authenticator will stop working. On next sign-in they’ll scan a new QR code. '
    + 'Their access and data are not affected.',
  resetConfirm: 'Reset two-step',
  selfInactive: 'You cannot deactivate your own account.',
} as const;

const AVAILABILITY_LABEL: Record<TStaffAvailability, string> = {
  'active': 'Active',
  'away': 'Away',
  'inactive': 'Inactive',
};

const AWAY_REASON_OPTION_LABEL: Record<TStaffAwayReason, string> = {
  'vacation': 'Vacation',
  'sick_leave': 'Sick leave',
  'other': 'Other',
};

const INVITE_STEPS: readonly TInviteStep[] = [
  { key: 'email', title: 'Invite email', caption: 'In their mailbox' },
  { key: 'link', title: 'Opens the link', caption: 'Within 24 hours' },
  { key: 'account', title: 'Name & password', caption: 'Set by them' },
  { key: 'totp', title: 'Two-step', caption: 'Authenticator app' },
];

const ADMIN_COLUMNS: readonly TAdminColumn[] = [
  { key: 'admin', width: '', label: 'Staff users' },
  { key: 'email', width: 'w-[18%]', label: 'Email' },
  { key: 'role', width: 'w-[12%]', label: 'Role' },
  { key: 'totp', width: 'w-[12%]', label: 'TOTP' },
  { key: 'calendar', width: 'w-[12%]', label: 'Calendar' },
  { key: 'active', width: 'w-[12%]', label: 'Last active' },
  { key: 'actions', width: 'w-[134px]', label: '' },
];

const MATRIX_COLUMNS: readonly TMatrixColumn[] = [
  {
    role: 'recruiter', label: 'Recruiter', width: 'w-[112px]', isLocked: false,
  },
  {
    role: 'recruiter_manager', label: 'Recruiter Manager', width: 'w-[112px]', isLocked: false,
  },
  {
    role: 'concierge', label: 'Concierge', width: 'w-[112px]', isLocked: false,
  },
  {
    role: 'concierge_manager', label: 'Concierge Manager', width: 'w-[112px]', isLocked: false,
  },
  {
    role: 'super_admin', label: 'Super Admin', width: 'w-[112px]', isLocked: true,
  },
];

const STAFF_SUB_ROLES: readonly TStaffSubRole[] = ['recruiter', 'recruiter_manager', 'concierge', 'concierge_manager', 'super_admin'];

export type {
  TAdminColumn,
  TAdminManageTab,
  TAdminManageTabID,
  THoldKindCopy,
  TInviteStep,
  TMatrixColumn,
};
export {
  ADMIN_COLUMNS,
  ADMIN_MANAGE_COPY,
  ADMIN_MANAGE_STAFF_PARAM,
  ADMIN_MANAGE_TAB_PARAM,
  ADMIN_MANAGE_TABS,
  AVAILABILITY_LABEL,
  AWAY_REASON_OPTION_LABEL,
  EDIT_COPY,
  INVITE_COPY,
  INVITE_ERROR_COPY,
  INVITE_STEPS,
  MATRIX_COLUMNS,
  MATRIX_COPY,
  HOLD_KIND_COPY,
  PENDING_INVITE_COPY,
  STAFF_CALENDAR_COPY,
  STAFF_SETTINGS_COPY,
  STAFF_SUB_ROLES,
  STAFF_TRANSITION_COPY,
  TRANSITION_BLOCKER_COPY,
  TRANSITION_ERROR_COPY,
  TRANSITION_KIND_TOKEN,
  ROLES_COPY,
  TOTP_COPY,
};
